import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Instagram, MessageCircle } from "lucide-react";
import { INSTAGRAM_URL, waLink } from "../config";
import StartFormModal from "./StartFormModal";

const EASE = [0.16, 1, 0.3, 1];

const STATS = [
  { value: "100%", label: "Individualizado" },
  { value: "48h", label: "Protocolo pronto" },
  { value: "24h", label: "Suporte em até 24h" },
  { value: "1:1", label: "Acompanhamento real" },
];

const LINES = [
  [{ t: "SEU CORPO" }],
  [{ t: "É O RESULTADO" }],
  [{ t: "DA SUA " }, { t: "DISCIPLINA.", outline: true }],
];

export default function Hero() {
  const [formOpen, setFormOpen] = useState(false);

  return (
    <section id="hero" className="relative overflow-hidden pt-32 pb-14 text-center md:pt-36">
      {/* soft white spotlight */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-[15]"
        style={{
          background:
            "radial-gradient(ellipse at 50% 0%, rgba(255,255,255,0.07) 0%, rgba(0,0,0,0) 55%)",
        }}
      />

      <div className="container-x relative">
        <h1 className="mx-auto font-display font-black uppercase leading-[0.9] tracking-tight text-[clamp(3rem,8vw,6.5rem)]">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: EASE }}
              >
                {line.map((part, j) => (
                  <span key={j} className={part.outline ? "text-stroke" : "text-white"}>
                    {part.t}
                  </span>
                ))}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="hero-instagram-link"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.55, ease: EASE }}
          className="mx-auto mt-5 inline-flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.35em] text-white/60 transition-colors hover:text-white"
        >
          <Instagram className="h-4 w-4" />
          @lucasfucht
        </motion.a>

        {/* Retrato recortado, sem brilho, com degradê na base */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="relative mx-auto w-fit"
        >
          <motion.img
            src="/hero-retrato-bw.webp"
            alt="Lucas Fucht — retrato em preto e branco"
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.65, ease: EASE }}
            className="retrato-edge-x relative z-10 mx-auto h-[420px] w-auto object-cover md:h-[520px]"
          />
        </motion.div>

        {/* Frase de efeito sobre o shape */}
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="relative z-20 mx-auto -mt-4 max-w-3xl font-display font-black uppercase leading-[1.02] tracking-tight text-4xl md:text-6xl"
        >
          Método e treino
          <br />
          <span className="text-stroke">
            para transformar seu físico
          </span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9, ease: EASE }}
          className="mx-auto mt-6 max-w-md text-balance text-base leading-relaxed text-white/60 md:text-lg"
        >
          Treino e dieta individualizados no app da Prime Coaching, com meu
          acompanhamento direto no WhatsApp.
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 1, ease: EASE }}
          className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-white/45"
        >
          Vagas limitadas por mês · Acompanhamento 100% online
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.1, ease: EASE }}
          className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row"
        >
          <button
            data-testid="hero-cta-button"
            onClick={() => setFormOpen(true)}
            className="btn-primary"
          >
            Iniciar minha transformação
            <ArrowRight className="h-5 w-5" />
          </button>
          <a
            data-testid="hero-whatsapp-button"
            href={waLink("Fala, Lucas! Tenho interesse na consultoria, mas queria tirar algumas dúvidas rápidas.")}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline"
          >
            <MessageCircle className="h-5 w-5" />
            Tirar dúvidas
          </a>
        </motion.div>

        {/* Stats band */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mt-16 grid grid-cols-2 gap-6 border-t border-white/10 pt-8 text-left md:grid-cols-4"
        >
          {STATS.map((s) => (
            <div key={s.label}>
              <p className="font-display text-4xl font-black tracking-tight text-white md:text-5xl">
                {s.value}
              </p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                {s.label}
              </p>
            </div>
          ))}
        </motion.div>
      </div>

      <StartFormModal open={formOpen} onClose={() => setFormOpen(false)} />
    </section>
  );
}
